import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGetPointManagerByUserQuery } from "api/baseAPI/pointManagers";
import { useGetUsersSelfQuery } from "api/baseAPI/user";
import {
  AllWalletsQueryParams,
  useGetAllWalletsQuery,
} from "api/baseAPI/wallets";
import { TableFilter } from "components/table/TableFilter";
import { MultiSelect } from "UI/Form/MultiSelect";
import { Grid } from "UI/Grid";
import { useFilterMakerResult } from "utils/filterMaker";

export const AllWalletsFilters = ({
  filters,
}: {
  filters: useFilterMakerResult<AllWalletsQueryParams>;
}) => {
  const { t } = useTranslation();
  const { values, setValue, errors } = filters;

  const getUsersSelfQuery = useGetUsersSelfQuery();
  const { data: pointManagersData } = useGetPointManagerByUserQuery(
    {
      user: getUsersSelfQuery?.data?.id || 0,
    },
    { skip: !getUsersSelfQuery?.data?.id }
  );
  const getAllWalletsId = useMemo(() => {
    if (!pointManagersData?.results?.length) return "";

    const allWalletIds = new Set<number>();
    pointManagersData?.results.forEach((manager) => {
      manager.available_wallets?.forEach((wallet) => {
        allWalletIds.add(wallet.id);
      });
    });

    return Array.from(allWalletIds).join(",");
  }, [pointManagersData]);

  const { data: walletsData } = useGetAllWalletsQuery(
    {
      page: "1",
      page_size: "100",
      search: "",
      id__in: getAllWalletsId,
      currency__in: "",
      point__in: "",
      ordering: "",
    },
    { skip: !getAllWalletsId }
  );

  const getWalletOptions = useMemo(() => {
    if (walletsData?.results?.length) {
      return walletsData.results.map((wallet) => ({
        value: String(wallet.id),
        label: `${wallet.id} | ${wallet.name}`,
      }));
    }
    return [];
  }, [walletsData]);

  const getCurrencyOptions = useMemo(() => {
    if (pointManagersData?.results?.length) {
      const currencies = new Set<string>();
      pointManagersData.results.forEach((manager) => {
        manager.available_wallets.forEach((wallet) => {
          currencies.add(wallet.currency);
        });
      });
      return Array.from(currencies).map((currency) => ({
        value: currency,
        label: currency,
      }));
    }
    return [];
  }, [pointManagersData]);

  const getPointOptions = useMemo(() => {
    if (pointManagersData?.results?.length) {
      const points = new Set<{ id: number; name: string }>();
      pointManagersData.results.forEach((manager) => {
        points.add({
          id: manager.point.id,
          name: manager.point.name,
        });
      });
      return Array.from(points).map((point) => ({
        value: String(point.id),
        label: `${point.id} | ${point.name}`,
      }));
    }
    return [];
  }, [pointManagersData]);

  return (
    <TableFilter
      onSubmit={filters.handleSubmit}
      onReset={filters.handleReset}
      disabledReset={filters.disabledReset}
      disableSubmit={!!errors}
      widthBox="60%"
    >
      <Grid container mt={12} hSpace={24} vSpace={24} smHSpace={8}>
        <Grid item sm={12} md={6}>
          <MultiSelect
            value={values.id__in ? values.id__in.split(",") : []}
            onChange={(value) => {
              setValue("id__in", value.join(","));
              if (value?.length) setValue("point__in", "");
            }}
            label={t("Кошелек")}
            placeholder={t("Все")}
            size="small"
            options={getWalletOptions}
          />
        </Grid>
        <Grid item sm={12} md={6}>
          <MultiSelect
            value={values.currency__in ? values.currency__in.split(",") : []}
            onChange={(value) => setValue("currency__in", value.join(","))}
            label={t("Валюта")}
            placeholder={t("Все")}
            size="small"
            options={getCurrencyOptions}
          />
        </Grid>
        <Grid item sm={12} md={6}>
          <MultiSelect
            value={values.point__in ? values.point__in.split(",") : []}
            onChange={(value) => setValue("point__in", value.join(","))}
            label={t("Точка")}
            placeholder={t("Все")}
            size="small"
            options={getPointOptions}
          />
        </Grid>
      </Grid>
    </TableFilter>
  );
};
